export type FontConfig = {
  font_family: string;
  font_size_pt: number;
  text_color: string;
  bold: boolean;
  italic: boolean;
};

export type HeadingStyleConfig = FontConfig & {
  bottom_border_enabled: boolean;
  bottom_border_color: string;
};

export type TableRegionConfig = FontConfig & {
  background_color: string;
};

export type AfternoonTableConfig = {
  header_row: TableRegionConfig;
  whole_table: TableRegionConfig;
  first_column: TableRegionConfig;
  total_row: TableRegionConfig;
  odd_rows: TableRegionConfig;
  even_rows: TableRegionConfig;
};

export type BulletCharacter = '\u2022' | '\u25CB' | '\u25A0' | '\u2013' | '\u25C6' | '\u25AA';

type BulletStyleConfig = {
  level_2_bullet: BulletCharacter;
  level_3_bullet: BulletCharacter;
  level_4_bullet: BulletCharacter;
};

export type HeaderAlignment = 'left' | 'right' | 'center';

type PageHeaderConfig = {
  alignment: HeaderAlignment;
};

type PageFooterConfig = {
  logo_alignment: HeaderAlignment;
  show_page_numbers: boolean;
};

export type ExportBrandingConfigData = {
  normal: FontConfig;
  body_text: FontConfig;
  heading_1: HeadingStyleConfig;
  heading_2: HeadingStyleConfig;
  heading_3: HeadingStyleConfig;
  heading_4: HeadingStyleConfig;
  afternoon_table: AfternoonTableConfig;
  bullet_styles: BulletStyleConfig;
  first_page_header: PageHeaderConfig;
  subsequent_page_header: PageHeaderConfig;
  different_first_page_header: boolean;
  footer: PageFooterConfig;
};

export type ExportBrandingResponse = {
  id: string;
  companyId: string;
  config: ExportBrandingConfigData;
  firstPageHeaderLogoS3Key: string | null;
  subsequentPageHeaderLogoS3Key: string | null;
  footerLogoS3Key: string | null;
  hasExportAssets: boolean;
  hasTitlePage: boolean;
};

export const BULLET_OPTIONS: { value: BulletCharacter; label: string }[] = [
  { value: '\u2022', label: '\u2022 Bullet' },
  { value: '\u25CB', label: '\u25CB Circle' },
  { value: '\u25A0', label: '\u25A0 Square' },
  { value: '\u2013', label: '\u2013 Dash' },
  { value: '\u25C6', label: '\u25C6 Diamond' },
  { value: '\u25AA', label: '\u25AA Small Square' },
];

export const FONT_OPTIONS = [
  'Calibri',
  'Arial',
  'Times New Roman',
  'Helvetica',
  'Georgia',
  'Verdana',
  'Trebuchet MS',
  'Garamond',
  'Cambria',
  'Century Gothic',
  'Aptos',
  'Book Antiqua',
  'Bookman Old Style',
  'Candara',
  'Consolas',
  'Constantia',
  'Corbel',
  'Courier New',
  'Franklin Gothic Medium',
  'Futura',
  'Gill Sans MT',
  'Impact',
  'Lato',
  'Lucida Console',
  'Lucida Sans Unicode',
  'Merriweather',
  'Montserrat',
  'Open Sans',
  'Palatino Linotype',
  'Roboto',
  'Segoe UI',
  'Source Sans Pro',
  'Tahoma',
  'Tw Cen MT',
];

export const DEFAULT_CONFIG: ExportBrandingConfigData = {
  normal: {
    font_family: 'Calibri',
    font_size_pt: 11,
    text_color: '000000',
    bold: false,
    italic: false,
  },
  body_text: {
    font_family: 'Calibri',
    font_size_pt: 11,
    text_color: '000000',
    bold: false,
    italic: false,
  },
  heading_1: {
    font_family: 'Calibri',
    font_size_pt: 24,
    text_color: '000000',
    bold: false,
    italic: false,
    bottom_border_enabled: false,
    bottom_border_color: '000000',
  },
  heading_2: {
    font_family: 'Calibri',
    font_size_pt: 18,
    text_color: '000000',
    bold: false,
    italic: false,
    bottom_border_enabled: false,
    bottom_border_color: '000000',
  },
  heading_3: {
    font_family: 'Calibri',
    font_size_pt: 14,
    text_color: '000000',
    bold: false,
    italic: false,
    bottom_border_enabled: false,
    bottom_border_color: '000000',
  },
  heading_4: {
    font_family: 'Calibri',
    font_size_pt: 12,
    text_color: '000000',
    bold: false,
    italic: false,
    bottom_border_enabled: false,
    bottom_border_color: '000000',
  },
  afternoon_table: {
    header_row: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: 'FFFFFF',
      bold: false,
      italic: false,
      background_color: '4472C4',
    },
    whole_table: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: '000000',
      bold: false,
      italic: false,
      background_color: 'FFFFFF',
    },
    first_column: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: '000000',
      bold: false,
      italic: false,
      background_color: 'FFFFFF',
    },
    total_row: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: '000000',
      bold: false,
      italic: false,
      background_color: 'D9E2F3',
    },
    odd_rows: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: '000000',
      bold: false,
      italic: false,
      background_color: 'D9E2F3',
    },
    even_rows: {
      font_family: 'Calibri',
      font_size_pt: 11,
      text_color: '000000',
      bold: false,
      italic: false,
      background_color: 'FFFFFF',
    },
  },
  bullet_styles: {
    level_2_bullet: '\u2022',
    level_3_bullet: '\u25CB',
    level_4_bullet: '\u25A0',
  },
  first_page_header: { alignment: 'left' },
  subsequent_page_header: { alignment: 'left' },
  different_first_page_header: true,
  footer: { logo_alignment: 'left', show_page_numbers: true },
};
